/**
 * CORE-BN — Front-of-House Attention Capacity · human surface
 *
 * Constitutional authority: CORE-BN "Operational / User Experience" and
 * "Provenance / Explainability Requirements".
 *
 * What this card is for
 * ---------------------
 * The capability record's Accessibility finding is the design brief:
 *
 *   "The front-of-house custody queue... presents a list with per-row
 *    aging; it does not present a shop-level state, so a user must infer
 *    saturation by reading and counting rows. Under exactly the
 *    conditions the capability is meant to detect — a saturated front of
 *    house — that inference requires attention the user does not have."
 *
 * So this card states the shop-level verdict in one line at the top of
 * the queue, and shows the arithmetic underneath it for anyone who wants
 * it. Reading the rows becomes optional rather than mandatory.
 *
 * What it must never become
 * -------------------------
 * The card names no advisor, shows no per-person figure, and offers no
 * ranking. Every sentence has "front of house" or "the shop" as its
 * subject. That is not a style preference — the constitution is explicit
 * that "an unexplained saturation signal will be read as a statement
 * about whoever is on the desk", and this card is the surface where that
 * misreading would happen.
 *
 * Nothing here is actionable by the system. There is no button that
 * paces intake, holds a booking or moves a person; CORE-BN is advisory
 * in Core and the surface reflects that by offering information only.
 */

import { AlertTriangle, HelpCircle, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  canShowFohSaturationState,
  describeFohBasis,
  fohClaimBasisLabel,
  fohSaturationLabel,
  fohSaturationTone,
  fohUtilizationBarPercent,
  formatAttentionMinutes,
  formatFohUtilization,
  fohInsufficientBasisLabel,
  type FohSaturationTone,
} from '@/lib/foh/attention/display';
import type { FohAttentionCapacityResult } from '@/lib/foh/attention';

const TONE_BADGE: Record<FohSaturationTone, string> = {
  neutral: 'bg-muted text-muted-foreground border-transparent',
  calm: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  watch: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
  warn: 'bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-500/40',
  critical: 'bg-destructive/15 text-destructive border-destructive/40',
};

const TONE_BAR: Record<FohSaturationTone, string> = {
  neutral: 'bg-muted-foreground/40',
  calm: 'bg-emerald-500',
  watch: 'bg-amber-500',
  warn: 'bg-orange-500',
  critical: 'bg-destructive',
};

export interface FohAttentionCapacityCardProps {
  result: FohAttentionCapacityResult | null;
  summary: string;
  isLoading?: boolean;
  className?: string;
}

export function FohAttentionCapacityCard({
  result,
  summary,
  isLoading = false,
  className,
}: FohAttentionCapacityCardProps) {
  if (isLoading && !result) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Front-of-house attention</CardTitle>
          <CardDescription>Estimating…</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // No result, or a result with no basis, or — critically — a result
  // whose contributing claims are missing. In every one of those cases
  // the card states the absence plainly instead of showing a number the
  // shop cannot act on. See `canShowFohSaturationState`.
  if (!canShowFohSaturationState(result)) {
    return (
      <FohAttentionUnknownCard result={result} className={className} />
    );
  }

  // Narrowed by `canShowFohSaturationState`.
  const r = result as FohAttentionCapacityResult;
  const tone = fohSaturationTone(r.state);
  const barPercent = fohUtilizationBarPercent(r);
  const shownClaims = r.contributingClaims.filter(
    (c) => c.contributed && c.attentionMinutes > 0,
  );
  const basisNotes = describeFohBasis(r);

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">Front-of-house attention</CardTitle>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={cn('font-medium', TONE_BADGE[tone])}
            >
              {fohSaturationLabel(r.state)}
            </Badge>
            {/* Responsibility 6. The word "estimate" is not a hedge here;
                it is a required disclosure. */}
            <Badge variant="outline" className="text-muted-foreground">
              Estimate
            </Badge>
          </div>
        </div>
        <CardDescription>{summary}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Consumption against capacity ---------------------------------- */}
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-2 text-sm">
            <span className="text-muted-foreground">
              Estimated demand on the next {Math.round(r.windowMinutes)} minutes
            </span>
            <span className="font-medium tabular-nums">
              {formatFohUtilization(r)}
            </span>
          </div>
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-muted"
            role="img"
            aria-label={`Front of house is at ${formatFohUtilization(r)} of its estimated attention capacity`}
          >
            <div
              className={cn('h-full rounded-full transition-all', TONE_BAR[tone])}
              style={{ width: `${barPercent}%` }}
            />
          </div>
          <div className="flex flex-wrap justify-between gap-x-4 text-xs text-muted-foreground">
            <span>
              {formatAttentionMinutes(r.estimatedConsumedAttentionMinutes)} of
              attention needed
            </span>
            <span>
              {formatAttentionMinutes(r.availableAttentionMinutes)} available ·{' '}
              {formatAttentionMinutes(r.headroomAttentionMinutes)} spare
            </span>
          </div>
        </div>

        {/* Contributing claims — mandatory, never collapsed away ---------- */}
        <div className="space-y-1.5">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            What is claiming attention
          </p>
          <ul className="space-y-1">
            {shownClaims.map((claim) => (
              <li
                key={claim.kind}
                className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-sm"
              >
                <span className="flex items-baseline gap-2">
                  <span className="tabular-nums font-medium">{claim.count}</span>
                  <span>{claim.label}</span>
                  {claim.dwellEscalation > 1 && (
                    <Badge
                      variant="outline"
                      className="border-amber-500/40 bg-amber-500/10 px-1.5 py-0 text-[10px] font-normal text-amber-700 dark:text-amber-300"
                      title="Aged items cost the front desk more attention than fresh ones"
                    >
                      aged ×{claim.dwellEscalation}
                    </Badge>
                  )}
                  {claim.presence && claim.presence.inferred > 0 && (
                    <Badge
                      variant="outline"
                      className="px-1.5 py-0 text-[10px] font-normal text-muted-foreground"
                      title="Taken from the appointment book — the shop records no arrival event, so these have not been physically confirmed"
                    >
                      {claim.presence.observed > 0
                        ? `${claim.presence.inferred} expected`
                        : 'expected'}
                    </Badge>
                  )}
                  {claim.weightSource === 'shop' && (
                    <Badge
                      variant="outline"
                      className="px-1.5 py-0 text-[10px] font-normal text-muted-foreground"
                      title="This weight was set by the shop, not by the OptiBay default"
                    >
                      shop weight
                    </Badge>
                  )}
                </span>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {formatAttentionMinutes(claim.attentionMinutes)} ·{' '}
                  {fohClaimBasisLabel(claim.basis)}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Basis — why this number is only an estimate -------------------- */}
        <div className="space-y-1 rounded-md border border-border/60 bg-muted/40 p-2.5">
          <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
            How this was worked out
          </p>
          <ul className="space-y-0.5 text-xs text-muted-foreground">
            {basisNotes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
          <p className="pt-1 text-xs text-muted-foreground">
            This describes the shop&apos;s staffing against its workload. It is
            not a measure of anyone&apos;s performance.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * The stated-absence card.
 *
 * CORE-BN's Hardening requirement, carried into its Definition of Done:
 * "the model must fail to a STATED ABSENCE rather than to a default
 * figure, because a fabricated capacity number is worse than no capacity
 * number." So this renders no percentage, no bar and no band — only what
 * is missing and the reassurance that the shop should carry on as usual.
 */
function FohAttentionUnknownCard({
  result,
  className,
}: {
  result: FohAttentionCapacityResult | null;
  className?: string;
}) {
  const reasons = result?.insufficientBasisReasons ?? [];
  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">Front-of-house attention</CardTitle>
          <Badge variant="outline" className={TONE_BADGE.neutral}>
            {fohSaturationLabel('unknown')}
          </Badge>
        </div>
        <CardDescription>
          There is not enough basis to estimate front-of-house load right now,
          so plan as you normally would.
        </CardDescription>
      </CardHeader>
      {reasons.length > 0 && (
        <CardContent>
          <ul className="space-y-1 text-xs text-muted-foreground">
            {reasons.map((reason) => (
              <li key={reason} className="flex items-start gap-1.5">
                {reason === 'no_foh_staffing' ? (
                  <HelpCircle
                    className="mt-0.5 h-3.5 w-3.5 shrink-0"
                    aria-hidden="true"
                  />
                ) : (
                  <AlertTriangle
                    className="mt-0.5 h-3.5 w-3.5 shrink-0"
                    aria-hidden="true"
                  />
                )}
                <span>{fohInsufficientBasisLabel(reason)}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      )}
    </Card>
  );
}
